import { Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { SelectionGrid, type SelectionGridColumn } from "src/components/ReferenceSearch";
import {
    referenceSearchPrimaryNameSx,
    referenceSearchSecondaryTextSx,
} from "src/components/ReferenceSearch/referenceSearchTextStyles";

import { getIngredientPickerEntityKey, useIngredientPickerRows } from "./useIngredientPickerRows";
import type { IngredientPickerEntity, IngredientPickerSource } from "./useIngredientPickerRows";

type IngredientPickerTableV2Props = {
    delayedSearch: string;
    selectedIngredientKeys: Set<string>;
    source: IngredientPickerSource;
    onSelect: (entity: IngredientPickerEntity) => void;
};

const NO_VALUE = "-";

const getCountryLabel = (origin: string | null | undefined): string => {
    if (!origin) {
        return NO_VALUE;
    }

    if (origin.length !== 2) {
        return origin;
    }

    const displayNames = new Intl.DisplayNames([navigator.language], { type: "region" });

    return displayNames.of(origin.toUpperCase()) ?? origin;
};

export const IngredientPickerTableV2 = (props: IngredientPickerTableV2Props) => {
    const { delayedSearch, selectedIngredientKeys, source, onSelect } = props;
    const { t } = useTranslation();
    const { rows, isLoading, totalItems, pagination, setPagination } = useIngredientPickerRows({
        delayedSearch,
        source,
    });
    const pageCount = totalItems === 0 ? 0 : Math.ceil(totalItems / pagination.pageSize);
    const columns: SelectionGridColumn<IngredientPickerEntity>[] = [
        {
            id: "name",
            render: (row) => (
                <Typography variant="body-bold" sx={referenceSearchPrimaryNameSx}>
                    {row.name}
                </Typography>
            ),
        },
        {
            id: "identifier",
            render: (row) => (
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {row.code ?? row.clientId ?? NO_VALUE}
                </Typography>
            ),
        },
        {
            id: "sector",
            render: (row) => (
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {row.cpcLabel ?? row.ingredientLine?.name ?? NO_VALUE}
                </Typography>
            ),
        },
        {
            id: "origin",
            render: (row) => (
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {getCountryLabel(row.defaultOrigin ?? row.origin)}
                </Typography>
            ),
        },
    ];

    const handlePageChange = (value: number) => {
        setPagination((currentPagination) => ({
            ...currentPagination,
            pageIndex: value - 1,
        }));
    };

    return (
        <SelectionGrid
            columns={columns}
            emptyContent={
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {t("common.noResults", "No results")}
                </Typography>
            }
            getRowId={getIngredientPickerEntityKey}
            isLoading={isLoading}
            isRowSelected={(row) => selectedIngredientKeys.has(getIngredientPickerEntityKey(row))}
            onPageChange={handlePageChange}
            onSelect={onSelect}
            page={pagination.pageIndex + 1}
            pageCount={pageCount}
            rows={rows}
        />
    );
};
